import { authenticatedApiRequest, cleanEmptyFields } from "./core";
import type { PaginatedResponse, RecordingFilters } from "./core";
import type { Recording, CreateStandaloneRecording, UpdateRecording, RecordingContract } from "@/types";

export interface PaginatedRecordingsResponse extends PaginatedResponse {
  results: Recording[];
}

export const recordingApi = {
  getRecording: async (id: string): Promise<Recording> => {
    return authenticatedApiRequest<Recording>(`/catalogue/recordings/${id}`);
  },
  getRecordings: async (filters?: RecordingFilters): Promise<PaginatedRecordingsResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedRecordingsResponse>(
      `/catalogue/recordings${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  createStandaloneRecording: async (
    recording: CreateStandaloneRecording,
  ): Promise<Recording> => {
    return authenticatedApiRequest<Recording>(`/catalogue/recordings`, {
      method: "POST",
      body: JSON.stringify(cleanEmptyFields(recording)),
    });
  },
  updateRecording: async (id: string, recording: UpdateRecording): Promise<Recording> => {
    return authenticatedApiRequest<Recording>(`/catalogue/recordings/${id}`, {
      method: "PATCH",
      body: JSON.stringify(recording),
    });
  },
  deleteRecording: async (id: string): Promise<void> => {
    await authenticatedApiRequest<void>(`/catalogue/recordings/${id}`, {
      method: "DELETE",
    });
  },
  addRecordingContributors: async (
    recordingId: string,
    contributors: Array<{ id: string; role: string }>,
  ): Promise<void> => {
    if (contributors.length === 0) return;

    for (const contributor of contributors)
      await authenticatedApiRequest(`/catalogue/recordings/${recordingId}/contributors`, {
        method: "POST",
        body: JSON.stringify({
          contributor: contributor.id,
          role: contributor.role,
        }),
      });
  },
  syncRecordingContributors: async (
    recordingId: string,
    contributors: Array<{ contributor: string; role: string }>,
  ): Promise<void> => {
    await authenticatedApiRequest(`/catalogue/recordings/${recordingId}/contributors/sync`, {
      method: "PUT",
      body: JSON.stringify({ contributors }),
    });
  },
  getRecordingContracts: async (recordingId: string): Promise<RecordingContract[]> => {
    return authenticatedApiRequest<RecordingContract[]>(
      `/catalogue/recordings/${recordingId}/contracts`,
    );
  },
  addRecordingContract: async (
    recordingId: string,
    contractId: string,
  ): Promise<RecordingContract> => {
    return authenticatedApiRequest<RecordingContract>(
      `/catalogue/recordings/${recordingId}/contracts`,
      {
        method: "POST",
        body: JSON.stringify({ contract: contractId }),
      },
    );
  },
  removeRecordingContract: async (recordingId: string, contractId: string): Promise<void> => {
    await authenticatedApiRequest<void>(
      `/catalogue/recordings/${recordingId}/contracts/${contractId}`,
      {
        method: "DELETE",
      },
    );
  },
  batchLinkRecordingContract: async (
    contractId: string,
    recordingIds: string[],
    forceLink = true,
  ): Promise<void> => {
    await authenticatedApiRequest<void>(`/catalogue/recordings/contracts/batch-link`, {
      method: "POST",
      body: JSON.stringify({
        contract_id: contractId,
        sound_recording_ids: recordingIds,
        force_link: forceLink,
      }),
    });
  },
};
